import api from './api';
import {
  Audit,
  AuditListResponse,
  AuditFilters,
  CreateAuditDTO,
  UpdateAuditDTO,
  CompleteAuditDTO,
  Evidence,
  AuditDetail,
} from '../types/audit.types';

const AUDITS_BASE = '/audits';

export const auditService = {
  /**
   * GET /api/audits - List audits with pagination and filters
   */
  async list(filters?: AuditFilters): Promise<AuditListResponse> {
    const params = new URLSearchParams();
    
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.business_id) params.append('business_id', filters.business_id.toString());
    if (filters?.user_id) params.append('user_id', filters.user_id.toString());
    if (filters?.form_id) params.append('form_id', filters.form_id.toString());

    const response = await api.get<AuditListResponse>(
      `${AUDITS_BASE}?${params.toString()}`
    );
    return response.data;
  },

  /**
   * GET /api/audits/:id - Get audit detail
   */
  async getById(id: number): Promise<AuditDetail> {
    const response = await api.get<AuditDetail>(`${AUDITS_BASE}/${id}`);
    return response.data;
  },

  /**
   * POST /api/audits - Create new audit
   */
  async create(data: CreateAuditDTO): Promise<{ success: boolean; data: Audit }> {
    const response = await api.post<{ success: boolean; data: Audit }>(
      AUDITS_BASE,
      data
    );
    return response.data;
  },

  /**
   * PUT /api/audits/:id - Save responses
   */
  async update(id: number, data: UpdateAuditDTO): Promise<{ success: boolean; message: string }> {
    const response = await api.put<{ success: boolean; message: string }>(
      `${AUDITS_BASE}/${id}`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/audits/:id/complete - Complete audit
   */
  async complete(
    id: number,
    data: CompleteAuditDTO
  ): Promise<{
    success: boolean;
    data: Audit;
    score: { obtained: number; maximum: number; percentage: number };
  }> {
    const response = await api.patch<{
      success: boolean;
      data: Audit;
      score: { obtained: number; maximum: number; percentage: number };
    }>(`${AUDITS_BASE}/${id}/complete`, data);
    return response.data;
  },

  /**
   * POST /api/audits/:id/evidence - Upload evidence (photo)
   */
  async uploadEvidence(
    auditId: number,
    data: {
      response_id: number;
      file_url: string;
      file_name: string;
      file_size: number;
      order: number;
    }
  ): Promise<{ success: boolean; data: Evidence }> {
    const response = await api.post<{ success: boolean; data: Evidence }>(
      `${AUDITS_BASE}/${auditId}/evidence`,
      data
    );
    return response.data;
  },

  /**
   * PATCH /api/audits/:id/reopen - Reabrir auditoría completada (solo admin)
   */
  async reopen(id: number): Promise<{ success: boolean; message: string; data: Audit }> {
    const response = await api.patch<{ success: boolean; message: string; data: Audit }>(
      `${AUDITS_BASE}/${id}/reopen`
    );
    return response.data;
  },

  /**
   * DELETE /api/audits/:id - Delete draft audit
   */
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(
      `${AUDITS_BASE}/${id}`
    );
    return response.data;
  },

  /**
   * GET /api/audits/:id/report/pdf - Download PDF report
   */
  async downloadPDF(id: number): Promise<void> {
    const response = await api.get(`${AUDITS_BASE}/${id}/report/pdf`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-${id}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  /**
   * GET /api/audits/:id/report/excel - Download Excel report
   */
  async downloadExcel(id: number): Promise<void> {
    const response = await api.get(`${AUDITS_BASE}/${id}/report/excel`, {
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `audit-${id}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  },
};

// Legacy export for backwards compatibility
/** @deprecated Use auditService instead */
export const auditoriaService = auditService;

export default auditService;
